import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import { ServiceForm } from '../ServiceForm';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { serviceApi } from '@/api/endpoints/services';
import { CreateServiceRequest, UpdateServiceRequest, ServiceLine } from '@/types/service';

interface CreateServiceModalProps {
  onSuccess?: () => void;
}

export const CreateServiceModal: React.FC<CreateServiceModalProps> = ({
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { modals, closeModal, refreshCallbacks } = useUIStore();
  const [loading, setLoading] = useState(false);

  const isOpen = modals.createService || false;

  const handleSubmit = async (data: CreateServiceRequest | UpdateServiceRequest, serviceLines?: ServiceLine[]) => {
    setLoading(true);
    try {
      // Step 1: Create the service
      const createdService = await serviceApi.createService(data as CreateServiceRequest);

      // Step 2: Create service lines if any exist
      if (serviceLines && serviceLines.length > 0 && createdService.code) {
        const serviceLineData = serviceLines.map(line => ({
          materialId: line.materialId,
          quantity: line.quantity,
          loseRate: line.loseRate,
        }));

        await serviceApi.createBulkServiceLines(createdService.code, serviceLineData);
      }

      toast.success('Success', t('services.messages.createSuccess'));

      // Trigger refresh in parent component
      refreshCallbacks.services?.();

      closeModal('createService');
      onSuccess?.();
    } catch (error: any) {
      console.error('Error creating service:', error);
      const errorMessage = error.message || t('services.messages.createError');
      toast.error('Error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async (data: CreateServiceRequest | UpdateServiceRequest, serviceLines?: ServiceLine[]) => {
    await handleSubmit(data, serviceLines);
  };

  const handleCancel = () => {
    closeModal('createService');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('services.modals.createTitle')}
      size="xl"
    >
      <ServiceForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onSaveAndClose={handleSaveAndClose}
        loading={loading}
      />
    </Modal>
  );
};
